import React from 'react';
import { removeTypenameGeneric } from '@ssg/common/Helpers/typenameHelper';
import { useStorageState } from '@ssg/common/Hooks/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useFlag } from '@unleash/proxy-client-react';
import { EvaluationQuestions } from '@ssg/common/Configurations/Endevalutaion';
import { FeatureFlagEnums } from '@ssg/common/FeatureFlagEnums';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { DeviationColor, DeviationInput, EvaluationCategoriesInput, EvaluationEnum, EvaluationInput, GetMobileV2DrivingSlip_drivingSlip_case, SignatureInputType } from 'GQL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { stringToByteArray } from '@ssg/common/Helpers/inputFileHelper';
import { ESDHFileFragment } from '@ssg/common/GraphQL';
import { formatDateForInput } from '@ssg/common/Helpers/dateToDateOnlyString';
import { checkFilesExist, tryGetFile } from './checkFilesExist';
import { ApiMutationKey, useCreateEndevaluationReport } from 'Store';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import TextAreaInput from './Inputs/TextAreaInput';
import ReportStaticInfo from './ReportStaticInfo';
import Radio from '@ssg/common/Components/Radio';
import BasePage from 'Components/Layout/BasePage';
import SignatureInput from './Inputs/SignatureInput';
import useDrivingSlipImages from './useDrivingSlipImages';
import TextInput from './Inputs/TextInput';
import Datepicker from '@ssg/common/Components/Datepicker';
import FormFieldHeader from '@ssg/common/Components/FormFieldHeader';
import Button from 'Components/Button';

interface Props {
	drivingSlipCase: GetMobileV2DrivingSlip_drivingSlip_case;
	caseNo: string;
	drivingSlipId: string;
}
interface EvaluationError {
	categoryIndex: number;
	questionIndex: number;
}
interface DeviationError {
	reportIndex: number;
	key: keyof DeviationInput;
}
const EvaluationOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yes', value: EvaluationEnum.YES },
	{ label: 'reports.asbestos.no', value: EvaluationEnum.NO },
	{ label: 'reports.asbestos.notRelevant', value: EvaluationEnum.NOT_RELEVANT },
];

const DeviationColorOptions: SelectOption[] = [
	{ label: 'reports.asbestos.yellow', value: DeviationColor.YELLOW },
	{ label: 'reports.asbestos.red', value: DeviationColor.RED },
];

const EMPTY_DEVIATION_FORM = (relatedEvaluationCategory: string, relatedEvaluationQuestion: string): DeviationInput => ({
	reportDate: formatDateForInput(new Date()),
	relatedEvaluationCategory,
	relatedEvaluationQuestion,
	action: '',
	actionDate: null,
	actionResponsible: '',
	cause: '',
	description: '',
	severityLevel: DeviationColor.NON_SELECTED,
});

const throwEvaluationError = (categoryIndex: number, questionIndex, evaluationErrors: EvaluationError[]): string | undefined => {
	const isError = evaluationErrors.filter(re => re.categoryIndex === categoryIndex && re.questionIndex === questionIndex).length > 0;
	if (isError) return 'error.required';
	return;
};
const throwRemarkError = (categoryIndex: number, questionIndex, remarkErrors: EvaluationError[]): string | undefined => {
	const isError = remarkErrors.filter(re => re.categoryIndex === categoryIndex && re.questionIndex === questionIndex).length > 0;
	if (isError) return 'error.required';
	return;
};

const throwDeviationError = (reportIndex: number, key: keyof DeviationInput, deviationErrors: DeviationError[]): string | undefined => {
	const isError = deviationErrors.filter(re => re.reportIndex === reportIndex && re.key === key).length > 0;
	if (isError) return 'error.required';
	return;
};

const EndevaluationReportForm: React.FC<Props> = ({ drivingSlipCase, caseNo, drivingSlipId }): React.ReactElement => {
	const { t } = useTranslation();

	const saveButtonReportsFlag = useFlag(FeatureFlagEnums.SAVE_BUTTON_REPORTS);
	const populateEmptyLocalstorage = (): EvaluationCategoriesInput[] => {
		const initialData: EvaluationCategoriesInput[] = [];

		EvaluationQuestions.forEach((category, i) => {
			const evaluations: EvaluationInput[] = category.questions.map(question => ({
				index: question.index,
				question: question.question,
				answer: EvaluationEnum.NOT_SELECTED,
				remark: '',
			}));

			initialData.push({
				index: i,
				title: category.category,
				evaluations,
			});
		});

		return initialData;
	};

	React.useEffect(() => {
		const formData = removeTypenameGeneric(drivingSlipCase.caseAsbestosReports?.endevaluationReportFormData);

		if (formData) {
			if (formData.evaluationCategories) {
				const evaluations = removeTypenameGeneric(formData.evaluationCategories);
				setEvaluationCategories(evaluations);
			}
			if (formData.deviations) {
				const sanitizedDeviations = removeTypenameGeneric(formData.deviations);
				setDeviations(sanitizedDeviations);
			}
			if (formData.signature) {
				const sanitizedSignatures = removeTypenameGeneric(formData.signature);
				setSignature(formData.signature);

				if (sanitizedSignatures.signatureDetails?.fileName === null || sanitizedSignatures.signatureDetails?.fileName === undefined) {
					return;
				}

				const fileName = sanitizedSignatures.signatureDetails.fileName;
				const fileToGet = [{ fileName, folder: undefined }];
				const existingFiles = checkFilesExist(fileToGet, images);

				const file = tryGetFile(fileName, images);
				existingFiles.length > 0 ? setSignatureFile(file) : setSignatureFile(null);
			}
		}
	}, [drivingSlipCase.caseAsbestosReports?.endevaluationReportFormData]);

	const useLocalStorage = <T,>(name: string, initialValue: T) => useStorageState<T>(window.localStorage, `${drivingSlipCase.id}-endevaluation-${name}`, initialValue);
	const [activeTab, setActiveTab] = React.useState<number>(0);
	const [evaluationCategories, setEvaluationCategories] = useLocalStorage<EvaluationCategoriesInput[]>('evaluationCategories', populateEmptyLocalstorage());

	const [deviations, setDeviations] = useLocalStorage<DeviationInput[]>('deviationReport', []);
	const [signature, setSignature] = useLocalStorage<SignatureInputType | null>('signature', {});
	const [signatureFile, setSignatureFile] = React.useState<ESDHFileFragment | null>();
	const [uploadingSignature, setUploadingSignature] = React.useState<boolean>(false);
	const [fileError, setFileError] = React.useState<boolean>(false);
	const [deviationErrors, setDeviationError] = React.useState<DeviationError[]>([]);
	const [saved, setSaved] = React.useState(false);
	const [loadingType, setLoadingType] = React.useState<'CREATE' | 'SAVE'>('CREATE');
	const { upload, images } = useDrivingSlipImages(caseNo, drivingSlipId);
	const [createEndevaluationReport, { loading: submitting }] = useCreateEndevaluationReport();

	const [evaluationErrors, setEvaluationError] = React.useState<EvaluationError[]>([]);
	const [remarkErrors, setRemarkErrors] = React.useState<EvaluationError[]>([]);

	React.useEffect(() => {
		setSaved(false);
	}, [signature?.signatureDetails?.fileName, deviations, evaluationCategories]);

	const handleEvaluationChange = (
		categoryIndex: number,
		questionIndex: number,
		key: keyof Pick<EvaluationInput, 'answer' | 'remark'>,
		value: EvaluationInput[keyof Pick<EvaluationInput, 'answer' | 'remark'>],
	) => {
		setEvaluationCategories(currentEvaluations => {
			return currentEvaluations.map(cat =>
				cat.index === categoryIndex
					? {
							...cat,
							evaluations: cat.evaluations?.map(evaluation => (evaluation?.index === questionIndex ? { ...evaluation, [key]: value } : evaluation)),
					  }
					: cat,
			);
		});
		const category = evaluationCategories.find(evaluation => evaluation.index === categoryIndex);
		const question = category?.evaluations?.find(e => e?.index === questionIndex)?.question;
		if (question === undefined || category?.title === undefined) {
			return;
		}
		if (key === 'answer' && value !== EvaluationEnum.NOT_RELEVANT) {
			removeRemark(categoryIndex, questionIndex);
		}
		if (key === 'answer' && value === EvaluationEnum.NO) {
			addDeviationForm(category?.title, question);
		}
		if (key === 'answer' && value !== EvaluationEnum.NO) {
			removeDeviationForm(question);
		}
	};

	const handleDeviationChange = (index: number, key: keyof DeviationInput, value: DeviationInput[keyof DeviationInput]) => {
		setDeviations(currentDeviations =>
			currentDeviations.map((deviation, i) =>
				i === index
					? {
							...deviation,
							[key]: value,
					  }
					: deviation,
			),
		);
	};
	function addDeviationForm(categoryTitle: string, question: string) {
		setDeviations(current => [...current, EMPTY_DEVIATION_FORM(categoryTitle, question)]);
		setActiveTab(deviations.length);
	}

	function removeDeviationForm(question: string) {
		setDeviations(current => current.filter(deviation => deviation.relatedEvaluationQuestion !== question));
		setActiveTab(0);
	}

	const removeRemark = (categoryIndex: number, questionIndex: number) => {
		setEvaluationCategories(currentEvaluations => {
			return currentEvaluations.map(cat =>
				cat.index === categoryIndex
					? {
							...cat,
							evaluations: cat.evaluations?.map(evaluation => (evaluation?.index === questionIndex ? { ...evaluation, remark: '' } : evaluation)),
					  }
					: cat,
			);
		});
	};

	/**
	 * Currently not used, left in the code so that we can handle errros, if we need to be able to generate the repport. Date 18.09.2025
	 */
	const errorCheck = (): boolean => {
		const evaluationErrors: EvaluationError[] = [];
		const remarkErrors: EvaluationError[] = [];
		const deviationErrors: DeviationError[] = [];

		evaluationCategories.forEach(
			(category, categoryIndex) =>
				category.evaluations?.forEach((evaluation, questionIndex) => {
					if (evaluation?.answer === EvaluationEnum.NOT_SELECTED) {
						evaluationErrors.push({ categoryIndex, questionIndex });
					}
					if (evaluation?.answer === EvaluationEnum.NOT_RELEVANT && evaluation?.remark === '') {
						remarkErrors.push({ categoryIndex, questionIndex });
					}
				}),
		);
		deviations.forEach((deviation, reportIndex) => {
			if (deviation.reportDate === '' || deviation.reportDate === 'NaN-aN-aN') {
				deviationErrors.push({ reportIndex, key: 'reportDate' });
			}
			if (deviation.severityLevel === DeviationColor.NON_SELECTED) {
				deviationErrors.push({ reportIndex, key: 'severityLevel' });
			}
			if (deviation.description === '') {
				deviationErrors.push({ reportIndex, key: 'description' });
			}
			if (deviation.cause === '') {
				deviationErrors.push({ reportIndex, key: 'cause' });
			}
		});

		setEvaluationError(evaluationErrors);
		setRemarkErrors(remarkErrors);
		setDeviationError(deviationErrors);

		return evaluationErrors.length > 0 || deviationErrors.length > 0 || remarkErrors.length > 0;
	};

	async function saveReport() {
		try {
			setLoadingType('SAVE');
			await createEndevaluationReport({
				variables: {
					caseId: drivingSlipCase.id,
					formData: { deviations, evaluationCategories, signature },
					onlySave: true,
				},
				queueOfflineMutationOptions: {
					key: ApiMutationKey.DrivingSlip_CreateEndevaluationReport,
					removePreviousEntry: true,
					findPreviousEntryPredicate(variables) {
						return variables.caseId === drivingSlipCase.id;
					},
				},
			});

			setSaved(true);
		} catch (e) {
			console.error(e);
		}
	}

	return (
		<BasePage title={t('reports.reportTypes.endevaluation')}>
			<IonGrid className="mb-4 text-sm">
				<IonRow>
					<ReportStaticInfo drivingSlipCase={drivingSlipCase} />
				</IonRow>

				{EvaluationQuestions.map((category, categoryIndex) => {
					return (
						<IonGrid key={category.category}>
							<div className="text-2xl mt-4 font-bold text-blue">{category.category}</div>
							{category.questions.map((question, questionIndex) => {
								const selectedAnswer =
									evaluationCategories.find(cat => cat.index === categoryIndex)?.evaluations?.find(evaluation => evaluation?.index === question.index)?.answer ??
									EvaluationEnum.NOT_SELECTED;
								return (
									<React.Fragment key={question.question}>
										<IonRow key={question.question}>
											<div className="ml-5 mt-5">
												<Radio
													horizontal
													className="mt-4 flex flex-row"
													title={question.question}
													options={EvaluationOptions}
													name={`${category.category}-${question.index}`}
													onChange={e => {
														const selectedAnswer = e.target.value as EvaluationEnum;
														handleEvaluationChange(categoryIndex, question.index, 'answer', selectedAnswer);
													}}
													value={selectedAnswer}
													errorMessage={throwEvaluationError(categoryIndex, questionIndex, evaluationErrors)}
												/>
											</div>
										</IonRow>
										<IonRow>
											<div className="ml-5 mt-5 w-full">
												{selectedAnswer === EvaluationEnum.NOT_RELEVANT && (
													<TextAreaInput
														label="reports.asbestos.remark"
														value={
															evaluationCategories.find(cat => cat.index === categoryIndex)?.evaluations?.find(evaluation => evaluation?.index === question.index)?.remark
														}
														onChange={e => {
															handleEvaluationChange(categoryIndex, question.index, 'remark', e);
														}}
														placeholder={'reports.asbestos.remarkPlaceholder'}
														errorMessage={throwRemarkError(categoryIndex, questionIndex, remarkErrors)}
													/>
												)}
											</div>
										</IonRow>
									</React.Fragment>
								);
							})}
						</IonGrid>
					);
				})}
				{deviations.length > 0 && (
					<>
						<div className="text-2xl mt-4 font-bold text-blue">{t('reports.asbestos.deviationTitles')}</div>
						<div className="flex flex-wrap">
							<div className="w-full">
								<ul className="mb-0 list-none flex-row flex-wrap pb-4" role="tablist">
									{deviations.map((deviation, idx) => (
										<li key={deviation.relatedEvaluationQuestion} className="-mb-px mr-2 flex-auto text-center last:mr-0">
											{idx === 0}{' '}
											<a
												className={
													'block rounded mt-2 px-5 py-3 text-xs font-bold uppercase leading-normal shadow-lg ' +
													(activeTab === idx ? 'bg-blue-calm text-white' : 'text-blue-calm bg-white')
												}
												onClick={e => {
													e.preventDefault();
													setActiveTab(idx);
												}}
												data-toggle="tab"
												href={'#link' + idx}
												role="tablist"
											>
												{typeof deviationErrors.find(deviation => deviation.reportIndex === idx) !== 'undefined' && (
													<FontAwesomeIcon icon={faExclamationTriangle} className="text-red mr-1" size="lg" />
												)}
												{deviation.relatedEvaluationCategory}
											</a>
										</li>
									))}

									<li className="-mb-px mr-2 flex-auto text-center last:mr-0"></li>
								</ul>
								<div className="relative mb-6 flex w-full min-w-0 flex-col break-words rounded bg-white shadow-lg">
									<div className="flex-auto px-4 py-5">
										<div className="tab-content tab-space">
											{deviations.map((deviation, idx) => (
												<IonGrid key={deviation.relatedEvaluationQuestion} className={activeTab === idx ? 'block' : 'hidden'} id={'#link' + idx}>
													<FormFieldHeader largeTitle title={`${t('reports.asbestos.deviationTitle')} - ${deviation.relatedEvaluationQuestion}`} />
													<div className="mt-4">
														<IonRow>
															<Datepicker
																title={'reports.asbestos.deviationReportDate'}
																name={`deviationReportDate-${idx}`}
																value={deviation.reportDate}
																onChange={e => handleDeviationChange(idx, 'reportDate', e.target.value)}
																errorMessage={throwDeviationError(idx, 'reportDate', deviationErrors)}
															/>
														</IonRow>
														<IonRow>
															<Radio
																name={`severityLevel-${idx}`}
																options={DeviationColorOptions}
																title={'reports.asbestos.deviationSeverity'}
																onChange={e => {
																	const color = e.target.value as DeviationColor;
																	handleDeviationChange(idx, 'severityLevel', color);
																}}
																value={deviation.severityLevel}
																horizontal
															/>
														</IonRow>
														<IonRow>
															<TextAreaInput
																label="reports.asbestos.description"
																value={deviation.description}
																onChange={e => handleDeviationChange(idx, 'description', e)}
																errorMessage={throwDeviationError(idx, 'description', deviationErrors)}
																placeholder={'reports.asbestos.descriptionPlaceholder'}
																autoGrowOverride={true}
															/>
														</IonRow>
														<IonRow>
															<TextAreaInput
																label="reports.asbestos.cause"
																value={deviation.cause}
																onChange={e => handleDeviationChange(idx, 'cause', e)}
																errorMessage={throwDeviationError(idx, 'cause', deviationErrors)}
																placeholder={'reports.asbestos.causePlaceholder'}
																autoGrowOverride={true}
															/>
														</IonRow>
														<IonRow>
															<TextAreaInput
																label="reports.asbestos.correctingAction"
																value={deviation.action}
																onChange={e => handleDeviationChange(idx, 'action', e)}
																placeholder={'reports.asbestos.actionPlaceholder'}
																autoGrowOverride={true}
															/>
														</IonRow>
														<IonRow>
															<TextInput
																label={'reports.asbestos.actionResponsible'}
																value={deviation.actionResponsible}
																onChange={e => handleDeviationChange(idx, 'actionResponsible', e)}
															/>
														</IonRow>
														<IonRow>
															<Datepicker
																title={'reports.asbestos.actionDate'}
																name={`actionDate-${idx}`}
																value={deviation.actionDate ?? ''}
																onChange={e =>
																	e.target.value === '' || e.target.value === 'NaN-aN-aN'
																		? handleDeviationChange(idx, 'actionDate', null)
																		: handleDeviationChange(idx, 'actionDate', e.target.value)
																}
															/>
														</IonRow>
													</div>
												</IonGrid>
											))}
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
				<div className="flex gap-2">
					<div className="text-2xl mt-2 font-bold text-blue">{t('reports.asbestos.signature')}</div>
					<div className="text-2xl mt-2 font-bold text-blue lowercase">{t('case.arearesponsible')}</div>
				</div>
				<SignatureInput
					required={false}
					label={''}
					isUploading={uploadingSignature}
					signatureFile={signatureFile ?? undefined}
					onClear={() => {
						setSignatureFile(undefined);
						setSignature(null);
					}}
					onSubmit={async signature => {
						const fileAsBytes = stringToByteArray(signature.base64);
						const filename = `signature-${Date.now()}.${signature.extension}`;
						try {
							setUploadingSignature(true);
							setFileError(false);

							const file = await upload({
								filename: filename,
								fileData: fileAsBytes,
								blob: signature.base64,
							});
							setSignature({ signatureDate: formatDateForInput(new Date()), signatureDetails: { fileName: filename } });
							setSignatureFile(file);
						} catch (error) {
							console.error(error);
							setFileError(true);
						} finally {
							setUploadingSignature(false);
						}
					}}
					errorMessage={fileError ? t('common.couldNotUpload') : undefined}
				/>
				<IonRow>
					<IonCol size="12">
						{saveButtonReportsFlag && (
							<Button expand="block" disabled={submitting || uploadingSignature} loading={submitting && loadingType === 'SAVE'} onClick={() => saveReport()}>
								{t(saved ? 'reports.saved' : 'reports.saveReport')}
							</Button>
						)}
					</IonCol>
				</IonRow>
			</IonGrid>
		</BasePage>
	);
};

export default EndevaluationReportForm;
